import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import { getSchoolRedirectPath } from "@/routes/utils";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import useEmployeesQuery from "@/services/employeeManagement/useEmployeeManagementMutation";
import { useGetActiveInvoiceCode } from "@/services/masterData/invoicecodes/useGetActiveInvoiceCodes";
import { useGetInvoiceDetailsByInstallmentIdQuery } from "@/services/school/commercial/installment/invoice/useGetInvoiceDetailsByInstallmentIdQuery";
import { useUpdateInvoiceMutation } from "@/services/school/commercial/installment/invoice/useUpdateInvoiceMutation";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  BANK_ACCOUNT_DETAILS,
  EXPECTED_INVOICE_CLEARANCE_DATE,
  EXPECTED_INVOICE_GENERATION_DATE,
  EXPECTED_INVOICE_SUBMISSION_DATE,
  INVOICE_CODE,
  INVOICE_FILE,
  INVOICE_GENERATION_DATE,
  INVOICE_OWNER,
  INVOICE_OWNER_ROLE,
  INVOICE_REMARKS,
  INVOICE_SUBMISSION_DATE,
} from "./consts";
import { formValues, schema } from "./validations";
import { useDownloadInvoice } from "@/services/school/commercial/installment/invoice/useDownloadInvoiceMutation";

const useInstallmentInvoice = () => {
  const navigate = useNavigate();
  const { action, id, installmentIndex } = useParams();
  const attachmentRef = useRef<any>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [openUpdatePdc, setOpenUpdatePdc] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isEdit = action === "edit" && !!id;
  const queryClient = useQueryClient();

  const loggedInUserData = useSelector(
    (state: RootState) => state.currentData.loggedInUserData
  );

  const form = useForm<formValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      [EXPECTED_INVOICE_GENERATION_DATE]: "",
      [EXPECTED_INVOICE_SUBMISSION_DATE]: "",
      [EXPECTED_INVOICE_CLEARANCE_DATE]: "",
      [INVOICE_GENERATION_DATE]: "",
      [INVOICE_CODE]: "",
      [INVOICE_FILE]: null,
      [INVOICE_OWNER]: "",
      [INVOICE_OWNER_ROLE]: "SALES_OPS",
      [BANK_ACCOUNT_DETAILS]: "",
      [INVOICE_SUBMISSION_DATE]: "",
      [INVOICE_REMARKS]: "",
    },
  });

  const requiredFields = getRequiredFields(schema);

  const toYMD = (value: any) => {
    if (!value) return "";
    const m = moment(value);
    return m.isValid() ? m.format("YYYY-MM-DD") : "";
  };

  // --- Ensure query runs only when we have necessary params (fixes "works only on reload") ---
  const queryEnabled = Boolean(isEdit && installmentIndex);

  const { data: invoiceResp, refetch: refetchInvoice } = useGetInvoiceDetailsByInstallmentIdQuery(
    String(installmentIndex) || "",
    { enabled: queryEnabled, refetchOnMount: "always", refetchOnWindowFocus: false } as any
  );


  // Also force a refetch when navigation occurs
  useEffect(() => {
    if (!queryEnabled) return;
    refetchInvoice?.();
  }, [installmentIndex, action, id, queryEnabled, refetchInvoice]);

  const { data: invoiceCodes, isPending: invoiceCodeLoading } =
    useGetActiveInvoiceCode();

  const { mutateAsync: downloadInvoice } = useDownloadInvoice();

  const { mutateAsync: updateInstallmentInvoice } = useUpdateInvoiceMutation({
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ["invoice-detail"] });
      // Refetch invoice data to get updated file UUID
      await queryClient.refetchQueries({ queryKey: ["invoice-detail"] });
    },
  });

  const watchedInvoiceOwnerRole = form.watch(INVOICE_OWNER_ROLE);

  const {
    data: employeeResp,
    isPending,
    isError,
    error,
  } = useEmployeesQuery(
    { role: watchedInvoiceOwnerRole || "SALES_OPS", status: true },
    {
      onError: (err) => {
        toast.error(err?.message || "Error fetching invoice owners");
      },
    }
  );

  // --- Helpers to extract IDs and normalize option lists ---
  const extractOwnerId = (ownerFromApi: any) => {
    if (!ownerFromApi) return "";
    if (typeof ownerFromApi === "string") return ownerFromApi;
    return (
      ownerFromApi.profile_id ??
      ownerFromApi.profileId ??
      ownerFromApi._id ??
      ownerFromApi.id ??
      ""
    );
  };

  const extractInvoiceCodeId = (codeFromApi: any) => {
    if (!codeFromApi) return "";
    return typeof codeFromApi === "object"
      ? codeFromApi._id ?? codeFromApi.id ?? ""
      : codeFromApi;
  };

  // Normalize employeeResp into an array of employee records
  const getEmployeeRecords = () => {
    const resp: any = employeeResp;
    if (!resp) return [];
    if (Array.isArray(resp)) return resp;
    if (resp?.data && Array.isArray(resp.data)) {
      const first = resp.data[0];
      if (first?.records && Array.isArray(first.records)) return first.records;
      return resp.data;
    }
    if (resp?.data?.[0]?.records && Array.isArray(resp.data[0].records)) {
      return resp.data[0].records;
    }
    // case where API returns a single employee object (like your log)
    if (typeof resp === "object" && resp.profile_id) return [resp];
    return [];
  };

  // Normalize invoice codes into an array
  const getInvoiceCodesList = () => {
    const resp: any = invoiceCodes;
    if (!resp) return [];
    if (Array.isArray(resp)) return resp;
    if (resp?.data && Array.isArray(resp.data)) return resp.data;
    if (typeof resp === "object") return [resp];
    return [];
  };

  // Create a minimal file-like object from a UUID so the preview can show something.
  // Adjust `name` / `url` shape if your preview expects different keys.
  const makeUploadedFileObject = (uploadedInvoice: any) => {
    if (!uploadedInvoice) return null;
    // If uploadedInvoice is already an object with name/url, return as-is
    if (typeof uploadedInvoice === "object" && (uploadedInvoice.name || uploadedInvoice.url || uploadedInvoice.uuid)) {
      return uploadedInvoice;
    }
    // If it's a UUID string, return a minimal object
    if (typeof uploadedInvoice === "string") {
      return {
        uuid: uploadedInvoice,
        name: `invoice-${uploadedInvoice}.pdf`, // minimal display name; adapt if you have actual filename
        // optionally include a URL if you have a download endpoint:
        // url: `/api/v1/invoices/download/${uploadedInvoice}`
      };
    }
    return null;
  };

  // --- If route params change while user navigates, ensure data is refetched ---
  useEffect(() => {
    if (!queryEnabled) return;
    queryClient.invalidateQueries({ queryKey: ["invoice-detail"] });
  }, [installmentIndex, action, id, queryEnabled, queryClient]);

  // --- Step 1: reset stable fields as soon as invoiceResp.data arrives ---
  useEffect(() => {
    if (!queryEnabled || !invoiceResp?.data) return;

    const currentInvoiceFile = form.getValues(INVOICE_FILE);
    const isFileObject = currentInvoiceFile instanceof File;

    const uploadedInvoiceFromApi = invoiceResp.data.uploadedInvoice ?? null;
    const invoiceFileValue = isFileObject
      ? currentInvoiceFile
      : makeUploadedFileObject(uploadedInvoiceFromApi);

    form.reset({
      [INVOICE_FILE]: invoiceFileValue,
      [EXPECTED_INVOICE_GENERATION_DATE]:
        toYMD(invoiceResp.data.expectedInvoiceGenerationDate) || "",
      [EXPECTED_INVOICE_SUBMISSION_DATE]:
        toYMD(invoiceResp.data.expectedInvoiceSubmissionDate) || "",
      [EXPECTED_INVOICE_CLEARANCE_DATE]:
        toYMD(invoiceResp.data.expectedInvoiceClearanceDate) || "",
      [INVOICE_GENERATION_DATE]: toYMD(invoiceResp.data.invoiceGenerationDate) || "",
      // leave owner/code empty for now; we'll set them in next effect after options are built
      [INVOICE_CODE]: "",
      [INVOICE_OWNER]: "",
      [BANK_ACCOUNT_DETAILS]: invoiceResp.data.bankAccountDetail || "",
      [INVOICE_SUBMISSION_DATE]: toYMD(invoiceResp.data.invoiceSubmissionDate) || "",
      [INVOICE_REMARKS]: invoiceResp.data.invoiceRemark || "",
    });

    queryClient.setQueryData(["current-invoice-id"], invoiceResp.data.invoiceId);
  }, [queryEnabled, invoiceResp?.data]); // depend on invoiceResp.data explicitly

  // --- Step 2: when employeeResp or invoiceCodes arrive, set matching select values and ensure options include invoice owner if missing ---
  useEffect(() => {
    if (!queryEnabled || !invoiceResp?.data) return;

    const ownerIdFromApi = extractOwnerId(invoiceResp.data.invoiceOwner);
    const codeIdFromApi = extractInvoiceCodeId(invoiceResp.data.invoiceCode);

    const employeeRecords = getEmployeeRecords();
    const invoiceCodesList = getInvoiceCodesList();

    // ownerOptions built from employeeRecords
    let ownerOptions =
      employeeRecords?.map((emp: any) => ({
        label: emp.name,
        value: emp.profile_id ?? emp._id ?? emp.id ?? "",
      })) || [];

    // If the invoice payload contains invoiceOwnerDetail, ensure it's present in ownerOptions
    const ownerDetail = invoiceResp.data.invoiceOwnerDetail;
    if (ownerDetail) {
      const ownerDetailValue = ownerDetail.profile_id ?? ownerDetail._id ?? "";
      const exists = ownerOptions.some((o:any) => o.value === ownerDetailValue);
      if (!exists && ownerDetailValue) {
        ownerOptions = [
          {
            label: ownerDetail.name ?? ownerDetailValue,
            value: ownerDetailValue,
          },
          ...ownerOptions,
        ];
      }
    }

    // invoice code options
    let invoiceCodeOptions =
      invoiceCodesList?.map((code: any) => ({
        label: code.name ?? code.code ?? code._id,
        value: code._id ?? code.id ?? code.code ?? "",
      })) || [];

    // If invoiceResp.data.invoiceCode exists as object, ensure it's present in invoiceCodeOptions
    const invoiceCodeFromResp = invoiceResp.data.invoiceCode;
    if (invoiceCodeFromResp) {
      
      const codeValue = invoiceCodeFromResp._id;
      const exists = invoiceCodeOptions.some((c:any) => c.value === codeValue);
      if (!exists && codeValue) {
        invoiceCodeOptions = [
          {
            label: invoiceCodeFromResp.name ?? codeValue,
            value: codeValue,
          },
          ...invoiceCodeOptions,
        ];
      }
    }

    // Try to find matching option (so Select shows label). Fallback to raw id to preserve backend data.
    const ownerMatch =
      ownerOptions.find((o:any) => o.value && ownerIdFromApi && o.value === ownerIdFromApi) ?? null;
    const codeMatch =
      invoiceCodeOptions.find((c:any) => c.value && codeIdFromApi && c.value === codeIdFromApi) ??
      null;

    if (ownerMatch) {
      form.setValue(INVOICE_OWNER, ownerMatch.value, { shouldDirty: false });
    } else if (ownerIdFromApi) {
      // set raw id as fallback (select might not display a label if no option exists)
      form.setValue(INVOICE_OWNER, ownerIdFromApi, { shouldDirty: false });
    }

    if (codeMatch) {
      form.setValue(INVOICE_CODE, codeMatch.value, { shouldDirty: false });
    } else if (codeIdFromApi) {
      form.setValue(INVOICE_CODE, codeIdFromApi, { shouldDirty: false });
    }
  }, [queryEnabled, invoiceResp?.data, employeeResp, invoiceCodes]);

  // Optional debugging (enable if needed)
  useEffect(() => {
    // console.debug("invoiceResp.data", invoiceResp?.data);
    // console.debug("employeeResp", employeeResp);
    // console.debug("invoiceCodes", invoiceCodes);
  }, [invoiceResp?.data, employeeResp, invoiceCodes]);

  const onSubmit = async (data: formValues) => {
    setIsLoading(true);
    const cleaned = setEmptyFieldsToNull(removeEmptyFields(data)) as any;

    try {
      await updateInstallmentInvoice({
        installmentId: String(invoiceResp?.data?.invoiceId),
        ...cleaned,
      });
      // File object is preserved in form state for display
      // The mutation handles uploading the file and storing the UUID on the backend
    } finally {
      setIsLoading(false);
    }
  };

  const onCancel = () => {
    const userRole = loggedInUserData?.role || [];
    navigate(getSchoolRedirectPath(userRole));
  };

  const onCancelUpdatePdc = () => {
    setOpenUpdatePdc(false);
  };

  const onUpdatePdcClick = () => {
    setOpenUpdatePdc(true);
  };

  const donwloadInvoice = async () => {
    if (!invoiceResp?.data?.invoiceId) {
      toast.error("No invoice file available for download");
      return;
    }
    downloadInvoice({
      invoiceId: invoiceResp?.data?.invoiceId,
      thirdPartyInvoice: invoiceResp?.data?.uploadedInvoice != null ? true : false,
    });
  };

  // Final computed options returned to the component:
  // include invoiceOwnerDetail (if present) at the top so label is available even if employeeResp misses it
  const computedEmployeeRecords = getEmployeeRecords();
  let invoiceOwnerOptions =
    computedEmployeeRecords.map((emp: any) => ({
      label: emp.name,
      value: emp.profile_id ?? emp._id ?? emp.id ?? "",
    })) || [];

  if (invoiceResp?.data?.invoiceOwnerDetail) {
    const detail = invoiceResp.data.invoiceOwnerDetail;
    const detailValue = detail.profile_id ?? detail._id ?? "";
    if (detailValue && !invoiceOwnerOptions.some((o:any) => o.value === detailValue)) {
      invoiceOwnerOptions = [
        { label: detail.name  ?? detailValue, value: detailValue },
        ...invoiceOwnerOptions,
      ];
    }
  }

  const invoiceCodesList = getInvoiceCodesList();
  const computedInvoiceCodeOptions =
    invoiceCodesList.map((code: any) => ({
      label: code.name ?? code.code ?? code._id,
      value: code._id ?? code.id ?? code.code ?? "",
    })) || [];


  const invoiceOwnerValue = form.watch(INVOICE_OWNER);

  // memoize options so identity only changes when underlying data changes
  const memoInvoiceOwnerOptions = useMemo(() => invoiceOwnerOptions, [JSON.stringify(invoiceOwnerOptions)]);
  const memoInvoiceCodeOptions = useMemo(() => computedInvoiceCodeOptions, [JSON.stringify(computedInvoiceCodeOptions)]);

  return {
    form,
    requiredFields,
    onSubmit,
    onCancel,
    attachmentRef,
    scrollRef,
    openUpdatePdc,
    onCancelUpdatePdc,
    onUpdatePdcClick,
    invoiceCodes,
    invoiceCodeLoading,
    isLoading,
    invoiceOwnerOptions: memoInvoiceOwnerOptions,
    invoiceCodeOptions: memoInvoiceCodeOptions,
    invoiceOwnerValue, // <-- expose current value
    donwloadInvoice,
  };
};

export default useInstallmentInvoice;
